// The combat feed: short, loud lines stacked in the corner of a raid, the way a shooter's kill
// feed reads. Missiles inbound, interceptions made and missed, aircraft and drones shot down,
// missiles striking home, warheads going off, with a running interception rate on top. Lines
// slide in, hold a few seconds and fade; a nuke's lines are bigger and hold longer.
import { DEFAULT_CONFIG, type CombatEvent } from '@thrash/sim';

const el = document.getElementById('feed')!;
const rateEl = el.querySelector<HTMLElement>('.rate')!;
const list = el.querySelector<HTMLElement>('.lines')!;

/** Most lines on screen at once; the oldest goes when a new one arrives. */
const MAX_LINES = 7;
const HOLD_MS = 4200, NUKE_HOLD_MS = 7000;

let intercepted = 0, leaked = 0;

const unitName = (t: string): string => (DEFAULT_CONFIG.units[t]?.name ?? t).toUpperCase();
const byName = (t: string): string => (DEFAULT_CONFIG.buildings[t]?.name ?? DEFAULT_CONFIG.units[t]?.name ?? t).toUpperCase();
const isMissile = (t: string): boolean => DEFAULT_CONFIG.units[t]?.domain === 'missile' || t === 'nuke' || t === 'emp';
/** The warheads that are nuclear: their lines are the big ones. */
export const NUCLEAR = new Set(['nuke', 'icbm', 'hgv_nuclear']);

function line(text: string, tone: 'threat' | 'good' | 'bad' | 'nuke', icon: string): void {
  const row = document.createElement('div');
  row.className = `line ${tone}`;
  row.innerHTML = `<span class="icon">${icon}</span><span class="text"></span>`;
  row.querySelector('.text')!.textContent = text;
  list.prepend(row);
  while (list.childElementCount > MAX_LINES) list.lastElementChild!.remove();
  const hold = tone === 'nuke' ? NUKE_HOLD_MS : HOLD_MS;
  setTimeout(() => row.classList.add('out'), hold);
  setTimeout(() => row.remove(), hold + 600);
  el.hidden = false;
}

function updateRate(): void {
  const total = intercepted + leaked;
  rateEl.textContent = total === 0 ? '' : `INTERCEPTION ${intercepted}/${total} · ${Math.round((intercepted * 100) / total)}%`;
}

/** A line of the raid's own: a wave coming in, say. */
export function feedNote(text: string, tone: 'threat' | 'good' | 'bad' | 'nuke' = 'threat', icon = '▶'): void {
  line(text, tone, icon);
}

/** What the sim reported this tick. */
export function feedEvents(events: readonly CombatEvent[]): void {
  for (const e of events) {
    switch (e.kind) {
      case 'inbound':
        if (NUCLEAR.has(e.what)) line(`NUCLEAR LAUNCH DETECTED · ${unitName(e.what)}${e.by ? ` · ${unitName(e.by)}` : ''}${e.aim ? ` · ${e.aim === 'hq' ? 'COMMAND CENTER' : byName(e.aim)}` : ''}`, 'nuke', '☢');
        else if (e.what === 'emp') line(`EMP MISSILE INBOUND${e.by ? ` · ${unitName(e.by)}` : ''}`, 'threat', '⚡');
        else line(`${unitName(e.what)} INBOUND${e.aim ? ` · ${e.aim === 'hq' ? 'COMMAND CENTER' : byName(e.aim)}` : ''}`, 'threat', '▲');
        break;
      case 'intercepted':
        intercepted++;
        if (NUCLEAR.has(e.what)) line(`NUKE INTERCEPTED · ${byName(e.by ?? '')}`, 'good', '☢');
        else if (e.what === 'emp_missile') line(`EMP MISSILE INTERCEPTED · ${byName(e.by ?? '')}`, 'good', '✓');
        else line(`${byName(e.by ?? '')} INTERCEPTED ${unitName(e.what)}`, 'good', '✓');
        break;
      case 'missed':
        line(`${byName(e.by ?? '')} MISSED ${unitName(e.what)}`, 'bad', '✗');
        break;
      case 'evaded':
        line(`${unitName(e.what)} EVADED ${byName(e.by ?? '')} · FLARES`, 'threat', '✦');
        break;
      case 'downed':
        line(`${byName(e.by ?? '')} DOWNED ${unitName(e.what)}`, 'good', '✈');
        break;
      case 'impact':
        if (isMissile(e.what)) leaked++;
        if (NUCLEAR.has(e.what)) line('NUCLEAR DETONATION', 'nuke', '☢');
        else line(`${unitName(e.what)} STRUCK HOME`, 'bad', '✹');
        break;
      case 'detonated':
        leaked++;
        if (e.what === 'nuke') line('NUCLEAR DETONATION', 'nuke', '☢');
        else if (e.what === 'emp') line('EMP DETONATED · ELECTRONICS DOWN', 'bad', '⚡');
        else line(`${unitName(e.what)} IMPACT`, 'bad', '✹');
        break;
      case 'destroyed':
        line(`${byName(e.what)} DESTROYED`, 'bad', '✹');
        break;
      case 'ability':
        if (e.what === 'artillery') line('ARTILLERY CALLED IN · 3 S', 'good', '◎');
        else if (e.what === 'smoke') line('SMOKE LAID', 'good', '☁');
        else line('EMERGENCY REPAIR', 'good', '+');
        break;
      case 'shelled':
        line(`SHELLS LANDED${e.by && e.by !== '0' ? ` · ${e.by} DOWN` : ''}`, 'good', '◎');
        break;
    }
  }
  updateRate();
}

/** A new raid: an empty feed and a fresh count. */
export function resetFeed(): void {
  intercepted = 0;
  leaked = 0;
  list.replaceChildren();
  rateEl.textContent = '';
  el.hidden = false;
}

export function hideFeed(): void {
  el.hidden = true;
}
